/*
 * The app's pages. Signed-out pages (sign in, two-step, invites) sit beside the signed-in app; the app checks the session
 * once and sends people to sign in or two-step when needed. The preview build keeps the address in memory (it runs inside
 * a frame), the real app uses the browser's address bar.
 */
import {
  createMemoryHistory,
  createRootRoute,
  createRoute,
  createRouter,
  Link,
  Navigate,
  Outlet,
  useNavigate,
  useParams,
  useRouterState,
} from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useSyncExternalStore, type ReactNode } from "react";
import { api, ApiError, companyStore, setCompany } from "../api/client";
import { useCompanies, useLanding, useMe, useSignInLook, type Landing } from "../api/hooks";
import { Icon, type IconName } from "../components/Icon";
import { Button, Loading } from "../components/parts";
import { PreviewBar } from "../demo/PreviewBar";
import { useToast } from "../components/overlays";
import { ForgotPassword, Handoff, InviteAccept, OfficeSignIn, ResetPassword, SignIn, TwoStep } from "../screens/auth/AuthScreens";
import { ClientForm, ClientPage, ClientsList } from "../screens/Clients";
import { Home } from "../screens/Home";
import { TodoFix } from "../screens/TodoFix";
import { AgencyClient, AgencyClients, AgencyHome, Deals, Projects, Proposals, Results, Tasks } from "../screens/Agency";
import { Privacy } from "../screens/Privacy";
import { ViewAs } from "../screens/ViewAs";
import { DisputeFilePage } from "../screens/DisputeFile";
import { DataSettings } from "../screens/settings/Data";
import { DeletedSettings } from "../screens/settings/Deleted";
import { InvoicePage, InvoicesList } from "../screens/Invoices";
import { PaymentPage, PaymentsPage } from "../screens/Payments";
import { CardSavedPage, PayPage, PublicQuotePage, ReviewPage, SaveCardPage, VisitReportPage } from "../screens/Public";
import { Hiring, ScheduleTools } from "../screens/Field";
import { ScheduleSettings } from "../screens/settings/Schedule";
import { Inbox } from "../screens/Inbox";
import { CrewVisit } from "../screens/CrewVisit";
import { MyTrades, MyTradesAsClient } from "../screens/MyTrades";
import { Unsubscribe } from "../screens/Unsubscribe";
import { ReceptionistPage } from "../screens/settings/Eunice";
import { QuoteForm, QuotePage, QuotesPage, type QuotesView } from "../screens/Quotes";
import { JobForm, JobPage, JobsList } from "../screens/Jobs";
import { MyDay } from "../screens/Other";
import { MySchedule } from "../screens/CrewRoute";
import { UpdateWatch } from "./UpdateWatch";
import { RequestPage, RequestsPage } from "../screens/Requests";
import { ProposalBuilder, ProposalSettingsPage, ProposalsPage } from "../screens/Proposals";
import { Insights } from "../screens/Insights";
import { Books } from "../screens/Books";
import { HeadOffice } from "../screens/HeadOffice";
import { Security } from "../screens/Security";
import { PlanSettings } from "../screens/settings/Plan";
import { BusinessInfo, KeysSettings, MessagesSettings, MoneySettings, SecuritySettings } from "../screens/settings/Settings";
import { PriceSettings } from "../screens/settings/Prices";
import {
  AutomationPage,
  AutomationsSettings,
  MESSAGE_SECTIONS,
  SectionMessages,
  type MessageSection,
} from "../screens/settings/Automations";
import { WebsiteSettings } from "../screens/settings/Website";
import { SettingsLayout, SettingsOverview } from "../screens/settings/Layout";
import { AlertsSettings, ReviewsSettings, TeamSettings } from "../screens/settings/More";
import { FeatureSettings } from "../screens/settings/Feature";
import { Marketing } from "../screens/Marketing";
import { AUTOMATION_KINDS, AUTOMATIONS } from "@steward/shared";
import { Schedule } from "../screens/Schedule";
import { SearchPhone } from "../screens/SearchPhone";
import { MyPay } from "../screens/MyPay";
import { MyTimeOff } from "../screens/MyTimeOff";
import { Payroll, TimeSheet } from "../screens/Payroll";
import { Team } from "../screens/Team";
import { VisitPage } from "../screens/Visit";
import { ClaudeSettings, ConnectClaude, takeAfterSignIn } from "../screens/ConnectClaude";
import { SessionProvider, useSession } from "./session";
import { Shell } from "./Shell";
import { leavingForSso, markSignedOut } from "./sso";

const rootRoute = createRootRoute({
  component: () => (
    <>
      <Outlet />
      {__PREVIEW__ ? null : <UpdateWatch />}
      {__PREVIEW__ ? <PreviewBar /> : null}
    </>
  ),
});

const signIn = createRoute({ getParentRoute: () => rootRoute, path: "/sign-in", component: SignIn });
// Crew sign in is /sign-in now; /sign-in/phone stays for links and home screen icons made before (owner, Oct 31).
const phoneSignIn = createRoute({ getParentRoute: () => rootRoute, path: "/sign-in/phone", component: SignIn });
const officeSignIn = createRoute({ getParentRoute: () => rootRoute, path: "/sign-in/office", component: OfficeSignIn });
const forgot = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forgot",
  validateSearch: (s: Record<string, unknown>): { email?: string } =>
    typeof s.email === "string" && s.email ? { email: s.email } : {},
  component: ForgotPassword,
});
const reset = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset/$token",
  component: function ResetRoute() {
    const { token } = reset.useParams();
    return <ResetPassword token={token} />;
  },
});
const twoStep = createRoute({
  getParentRoute: () => rootRoute,
  path: "/two-step",
  validateSearch: (s: Record<string, unknown>) => ({ mode: s.mode === "setup" ? ("setup" as const) : ("needed" as const) }),
  component: TwoStep,
});
// The new tab a company switch opens at another brand's address.
const handoff = createRoute({ getParentRoute: () => rootRoute, path: "/handoff", component: Handoff });
const invite = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invite/$token",
  component: function InviteRoute() {
    const { token } = invite.useParams();
    return <InviteAccept token={token} />;
  },
});

/** An old long link code (the old Steward's, or this app's first). These links now go on to the short link. */
const isOldCode = (token: string) => /^([a-f0-9]{16}|[a-f0-9]{32}|[a-f0-9]{64})$/.test(token);

/**
 * A link sent with an old long code goes on to its short link (/i/, /q/, /r/), so the address bar and anything copied
 * from it stay short. Not found: the page itself says the link is wrong.
 */
function ShortLink({ kind, token, children }: { kind: "invoice" | "quote" | "review"; token: string; children: ReactNode }) {
  const old = isOldCode(token);
  const short = useQuery({
    queryKey: ["short-code", kind, token],
    queryFn: () => api<{ token: string }>("GET", `public/link/${kind}/${token}`),
    enabled: old,
    retry: false,
  });
  if (!old || short.isError) return children;
  if (!short.data) return <Loading />;
  const to = kind === "invoice" ? "/i/$token" : kind === "quote" ? "/q/$token" : "/r/$token";
  return <Navigate to={to} params={{ token: short.data.token }} replace />;
}

// The client's pay and quote pages: no sign in, outside the app.
const payPage = createRoute({
  getParentRoute: () => rootRoute,
  path: "/pay/$token",
  validateSearch: (s: Record<string, unknown>): { session?: string } =>
    typeof s.payment_intent === "string" && s.payment_intent ? { session: s.payment_intent } : {},
  component: function PayRoute() {
    const { token } = payPage.useParams();
    const { session } = payPage.useSearch();
    return (
      <ShortLink kind="invoice" token={token}>
        <PayPage key={token} token={token} {...(session ? { session } : {})} />
      </ShortLink>
    );
  },
});
// The short pay link the server sends now (/i/<token>, owner Oct 31). The same page, so Stripe's return lands back here.
const payShort = createRoute({
  getParentRoute: () => rootRoute,
  path: "/i/$token",
  validateSearch: (s: Record<string, unknown>): { session?: string } =>
    typeof s.payment_intent === "string" && s.payment_intent ? { session: s.payment_intent } : {},
  component: function PayShortRoute() {
    const { token } = payShort.useParams();
    const { session } = payShort.useSearch();
    return <PayPage key={token} token={token} {...(session ? { session } : {})} />;
  },
});
// Pay links sent by the old WordPress Steward (/invoice/<code>/, and /gm-invoice/<code>/ before 2.95.0) keep working:
// the copy keeps each invoice's old code (draft 15), and /pay/ sends it on to the short link.
const oldInvoiceLink = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invoice/$token",
  component: function OldInvoiceRoute() {
    const { token } = oldInvoiceLink.useParams();
    return <Navigate to="/pay/$token" params={{ token }} replace />;
  },
});
const olderInvoiceLink = createRoute({
  getParentRoute: () => rootRoute,
  path: "/gm-invoice/$token",
  component: function OlderInvoiceRoute() {
    const { token } = olderInvoiceLink.useParams();
    return <Navigate to="/pay/$token" params={{ token }} replace />;
  },
});
// The client's review page from a review request (old Steward links /review/<code>/ keep working: the copy keeps the code).
const reviewPage = createRoute({
  getParentRoute: () => rootRoute,
  path: "/review/$token",
  component: function ReviewRoute() {
    const { token } = reviewPage.useParams();
    return (
      <ShortLink kind="review" token={token}>
        <ReviewPage key={token} token={token} />
      </ShortLink>
    );
  },
});
// The short review link the server sends now (/r/<token>).
const reviewShort = createRoute({
  getParentRoute: () => rootRoute,
  path: "/r/$token",
  component: function ReviewShortRoute() {
    const { token } = reviewShort.useParams();
    return <ReviewPage key={token} token={token} />;
  },
});
// Quote links from before 2.95.0 (/gm-quote/<code>/).
const olderQuoteLink = createRoute({
  getParentRoute: () => rootRoute,
  path: "/gm-quote/$token",
  component: function OlderQuoteRoute() {
    const { token } = olderQuoteLink.useParams();
    return <Navigate to="/quote/$token" params={{ token }} replace />;
  },
});
// Back from Stripe's page after a card link (Clients, Send card link).
const cardSaved = createRoute({
  getParentRoute: () => rootRoute,
  path: "/card-saved",
  validateSearch: (s: Record<string, unknown>): { back?: string } => (s.back ? { back: "1" } : {}),
  component: function CardSavedRoute() {
    const { back } = cardSaved.useSearch();
    return <CardSavedPage saved={!back} />;
  },
});
// Our own save a card page, from the office's Text them a link to save a card.
const saveCard = createRoute({
  getParentRoute: () => rootRoute,
  path: "/card/$token",
  component: function SaveCardRoute() {
    const { token } = saveCard.useParams();
    return <SaveCardPage key={token} token={token} />;
  },
});
const crewVisit = createRoute({
  getParentRoute: () => app,
  path: "/my-day/$ref",
  component: function CrewVisitRoute(): ReactNode {
    const { ref } = crewVisit.useParams();
    return (
      <Allowed action="myvisit.update">
        <CrewVisit key={ref} visitRef={ref} />
      </Allowed>
    );
  },
});
const myTrades = createRoute({ getParentRoute: () => rootRoute, path: "/my", component: MyTrades });
/** The unsubscribe link in campaign emails: public, no sign in. */
const unsubscribe = createRoute({ getParentRoute: () => rootRoute, path: "/my/unsubscribe", component: Unsubscribe });
/** View as client: full screen like the client's own hub, outside the office menu. The server checks client.view_hub. */
const asClient = createRoute({
  getParentRoute: () => rootRoute,
  path: "/clients/$id/as-client",
  component: function AsClientRoute(): ReactNode {
    const { id } = asClient.useParams();
    const navigate = useNavigate();
    return <MyTradesAsClient key={id} clientId={id} onBack={() => void navigate({ to: "/clients/$id", params: { id } })} />;
  },
});
const connectClaude = createRoute({ getParentRoute: () => rootRoute, path: "/connect/claude", component: ConnectClaude });
const privacy = createRoute({ getParentRoute: () => rootRoute, path: "/privacy", component: Privacy });
/** View as, one visit: the visit as that crew member sees it, read only (owner, 2026-11-02). */
const viewAsVisit = createRoute({
  getParentRoute: () => app,
  path: "/hr/team/$personId/visit/$ref",
  component: function ViewAsVisitRoute(): ReactNode {
    const { personId, ref } = viewAsVisit.useParams();
    return (
      <Allowed action="security.read">
        <CrewVisit key={`${personId}:${ref}`} visitRef={ref} viewAs={personId} />
      </Allowed>
    );
  },
});
const viewAs = createRoute({
  getParentRoute: () => app,
  path: "/hr/team/$personId/day",
  component: function ViewAsRoute(): ReactNode {
    const { personId } = viewAs.useParams();
    return (
      <Allowed action="security.read">
        <ViewAs key={personId} personId={personId} />
      </Allowed>
    );
  },
});
const reportPublic = createRoute({
  getParentRoute: () => rootRoute,
  path: "/report/$token",
  component: function ReportRoute(): ReactNode {
    const { token } = reportPublic.useParams();
    return <VisitReportPage key={token} token={token} />;
  },
});
const quotePublic = createRoute({
  getParentRoute: () => rootRoute,
  path: "/quote/$token",
  component: function QuotePublicRoute() {
    const { token } = quotePublic.useParams();
    return (
      <ShortLink kind="quote" token={token}>
        <PublicQuotePage key={token} token={token} />
      </ShortLink>
    );
  },
});
// The office's look at the client's quote page before sending, drafts too (signed in).
const quotePreview = createRoute({
  getParentRoute: () => rootRoute,
  path: "/preview/quote/$id",
  component: function QuotePreviewRoute() {
    const { id } = quotePreview.useParams();
    return <PublicQuotePage key={id} token="" previewId={id} />;
  },
});
// The short quote link the server sends now (/q/<token>).
const quoteShort = createRoute({
  getParentRoute: () => rootRoute,
  path: "/q/$token",
  component: function QuoteShortRoute() {
    const { token } = quoteShort.useParams();
    return <PublicQuotePage key={token} token={token} />;
  },
});

/**
 * "https://app.thecleanersusa.com" and "https://steward.thecleanersusa.com" are one brand: the same domain past the first
 * part. Owner, Oct 31: on the old app. address the switcher dropped The Cleaners of New Orleans (its address is steward.).
 */
function sameBrandAddress(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  if (a === b) return true;
  const domain = (u: string) => {
    try {
      const parts = new URL(u).hostname.split(".");
      return parts.length > 2 ? parts.slice(1).join(".") : null;
    } catch {
      return null;
    }
  };
  const d = domain(a);
  return d !== null && d === domain(b);
}

/** Signed in and two-step done, with a company picked: then the app. Otherwise off to the right step. */
function SignedIn() {
  const me = useMe();
  const companies = useCompanies();
  const landing = useLanding();
  const look = useSignInLook();
  const qc = useQueryClient();
  const toast = useToast();
  const chosen = useSyncExternalStore(companyStore.subscribe, companyStore.get);
  const list = companies.data ?? [];
  const land = landing.data;
  // At a brand's own address (app.thecleanersusa.com) the app opens in that brand: a company saved from another brand is
  // set aside, so the switcher still reaches the rest but each visit starts here. Elsewhere the saved company comes first.
  const here = land?.kind === "brand" ? window.location.origin : null;
  // The saved company if the login still has it, else where this address starts them, else where they work (owners start
  // at the top of their tree).
  const company =
    list.find((c) => c.id === chosen && (!here || sameBrandAddress(c.linkUrl, here))) ??
    list.find((c) => c.id === (land?.kind === "no_access" ? null : land?.companyId)) ??
    list.find((c) => c.role !== "owner") ??
    list.find((c) => !list.some((x) => x.id === c.parentId)) ??
    list[0];
  const blocked = land?.kind === "no_access";
  useEffect(() => {
    if (company && company.id !== chosen && !blocked) setCompany(company.id);
  }, [company, chosen, blocked]);
  const switchHere = (id: string) => {
    setCompany(id);
    void qc.invalidateQueries();
  };
  // A company at another brand's address opens in a new tab there, signed in as this login (auth/handoff). The tab opens
  // on the click itself so the browser doesn't block it, and gets its address when the server answers. Each company's tab
  // has its own name, so switching again goes back to the tab already open instead of opening another.
  const switchTo = (id: string) => {
    const target = list.find((c) => c.id === id)?.linkUrl;
    if (__PREVIEW__ || !target || sameBrandAddress(target, window.location.origin)) {
      switchHere(id);
      return;
    }
    const tab = window.open("", `steward-${id}`);
    let fresh = true;
    try {
      // A new tab is a blank page we can read; one already on the brand's address is another site and throws.
      fresh = tab?.location.href === "about:blank";
    } catch {
      fresh = false;
    }
    if (tab && !fresh) {
      tab.focus();
      return;
    }
    if (tab) {
      // The opener stays: without it the browser forgets the tab's name and the next switch opens another. Both are ours.
      // The blank tab takes this page's background while the server answers, so it doesn't flash white.
      tab.document.title = "Steward";
      tab.document.body.style.cssText = `margin:0;background:${getComputedStyle(document.body).backgroundColor}`;
    }
    api<{ url: string | null }>("POST", "auth/handoff", { companyId: id }).then(
      ({ url }) => {
        if (!url) {
          tab?.close();
          switchHere(id);
        } else if (tab) tab.location.href = url;
        else window.location.assign(url);
      },
      (err: unknown) => {
        tab?.close();
        toast(err instanceof ApiError ? err.message : "That company didn't open. Try again.", { kind: "error" });
      },
    );
  };

  const err = me.error ?? companies.error;
  if (err instanceof ApiError) {
    if (err.code === "two_step_needed") return <Navigate to="/two-step" search={{ mode: "needed" }} />;
    if (err.code === "two_step_setup_needed") return <Navigate to="/two-step" search={{ mode: "setup" }} />;
    // Signed in at another brand's address? Head office's address knows, and brings this page back signed in (app/sso.ts).
    if (look.isLoading || leavingForSso(look.data?.sso)) return <Loading />;
    return <Navigate to="/sign-in" />;
  }
  if (me.isLoading || companies.isLoading || landing.isLoading || !me.data) return <Loading />;
  if (land?.kind === "no_access") return <NotHere landing={land} />;
  if (!company) {
    return (
      <div className="auth-main">
        <div className="auth-panel">
          <h1>No company yet</h1>
          <p className="lead">Your login isn't part of a company. Ask the owner to invite you.</p>
        </div>
      </div>
    );
  }
  return (
    <SessionProvider me={me.data} companies={list} company={company} onSwitch={switchTo}>
      <Shell />
    </SessionProvider>
  );
}

/** Signed in at a brand's address with no company there: a plain note and where their own companies are, nothing else. */
function NotHere({ landing }: { landing: Extract<Landing, { kind: "no_access" }> }) {
  const signOut = async () => {
    markSignedOut();
    await api("POST", "auth/sign-out").catch(() => null);
    window.location.assign("/sign-in");
  };
  return (
    <div className="auth-main">
      <div className="auth-panel">
        <h1>This is {landing.brand}</h1>
        <p className="lead">
          Your login isn't part of {landing.brand}. Open your own company below, or ask the {landing.brand} owner to invite you.
        </p>
        {landing.elsewhere.map((e) => (
          <a key={e.url} className="btn primary full" href={e.url}>
            Open {e.name}
          </a>
        ))}
        <Button type="tertiary" onClick={() => void signOut()}>
          Sign out
        </Button>
      </div>
    </div>
  );
}

const app = createRoute({ getParentRoute: () => rootRoute, id: "app", component: SignedIn });

function Start() {
  const { role } = useSession();
  // Signed in from Connect Claude: back to its page to press Allow.
  const after = takeAfterSignIn();
  if (after) {
    window.location.replace(after);
    return null;
  }
  return <Navigate to={role === "crew" ? "/my-day" : "/home"} />;
}

/** Only for roles that may see it; others land on their start page. */
function Allowed({ action, children }: { action: Parameters<ReturnType<typeof useSession>["can"]>[0]; children: ReactNode }) {
  const { can } = useSession();
  return can(action) ? <>{children}</> : <Start />;
}

/** My day (the crew's Home) and My schedule are the crew's pages; the office goes to Home and Schedule. */
function CrewOnly({ page }: { page?: "schedule" }) {
  const { role } = useSession();
  if (role !== "crew") return <Navigate to={page ? "/schedule" : "/home"} />;
  return page ? <MySchedule /> : <MyDay />;
}

/** The agency (The Marketers) gets its own screen in place of the home service one (draft 13). */
function ByTrade({ agency, other }: { agency: ReactNode; other: ReactNode }) {
  const { company } = useSession();
  return <>{company.trade === "agency" ? agency : other}</>;
}

/** An agency only page; anywhere else it goes Home. */
function AgencyOnly({ children }: { children: ReactNode }) {
  const { company } = useSession();
  return company.trade === "agency" ? <>{children}</> : <Navigate to="/home" />;
}

const page = <P extends string>(path: P, component: () => ReactNode) =>
  createRoute({ getParentRoute: () => app, path, component });

/** HR's side menu, like Marketing's (2026-10-30: the row of tabs on top read poorly). Payroll first, it is opened most (2026-11-02). */
const HR_MENU: [string, string, IconName][] = [
  ["/hr/payroll", "Payroll", "money"],
  ["/hr/team", "Team", "team"],
  ["/hr/time", "Time", "clock"],
  ["/hr/hiring", "Hiring", "person"],
];

function HrTabs({ children }: { children: ReactNode }) {
  const path = useRouterState({ select: (s) => s.location.pathname });
  return (
    <div className="settings-layout section-layout">
      <aside className="settings-side section-side">
        <nav className="subnav" aria-label="HR">
          <div className="subnav-group">
            <span className="subnav-title">HR</span>
            {HR_MENU.map(([to, label, icon]) => (
              <Link key={to} to={to} data-status={path === to ? "active" : undefined}>
                <Icon name={icon} size={16} />
                {label}
              </Link>
            ))}
          </div>
        </nav>
      </aside>
      <div className="settings-main">{children}</div>
    </div>
  );
}

const settings = createRoute({ getParentRoute: () => app, path: "/settings", component: SettingsLayout });
const setting = <P extends string>(path: P, component: () => ReactNode) =>
  createRoute({ getParentRoute: () => settings, path, component });
/** A settings page that moved to its feature: old links and bookmarks land there, at the same card. */
function Moved({ to }: { to: string }) {
  return <Navigate to={to} hash replace />;
}
/** The Inbox; ?thread= opens that conversation (the receptionist page links each reply here). */
const inboxPage = createRoute({
  getParentRoute: () => app,
  path: "/inbox",
  validateSearch: (s: Record<string, unknown>): { thread?: string } =>
    typeof s.thread === "string" && /^[0-9a-f-]{36}$/.test(s.thread) ? { thread: s.thread } : {},
  component: function InboxRoute() {
    const { thread } = inboxPage.useSearch();
    return (
      <Allowed action="client.read">
        <Inbox key={thread ?? ""} {...(thread ? { open: thread } : {})} />
      </Allowed>
    );
  },
});
const automationPage = createRoute({
  getParentRoute: () => settings,
  path: "/automations/$kind",
  component: function AutomationRoute(): ReactNode {
    const { kind } = automationPage.useParams();
    const k = AUTOMATION_KINDS.find((x) => x === kind);
    return k ? (
      <Allowed action="settings.read">
        <AutomationPage kind={k} />
      </Allowed>
    ) : (
      <Navigate to="/settings/automations" />
    );
  },
});
/** A section's reminder page (Quote settings, then Quote follow-up), the same page as in Settings but inside the section. */
const sectionMessagePage = <P extends string>(path: P, section: MessageSection) => {
  const r = createRoute({
    getParentRoute: () => app,
    path,
    component: function SectionMessageRoute(): ReactNode {
      const { kind } = useParams({ strict: false });
      const s = MESSAGE_SECTIONS[section];
      const k = s.kinds.find((x) => x === kind);
      return k ? (
        <Allowed action="settings.read">
          <FeatureSettings title={AUTOMATIONS[k].title} back={s.to} backLabel={s.label}>
            <AutomationPage kind={k} back={null} />
          </FeatureSettings>
        </Allowed>
      ) : (
        <Navigate to={s.to} />
      );
    },
  });
  return r;
};
const requestPage = createRoute({
  getParentRoute: () => app,
  path: "/requests/$id",
  component: function RequestRoute() {
    const { id } = requestPage.useParams();
    return (
      <Allowed action="lead.read">
        <RequestPage key={id} id={id} />
      </Allowed>
    );
  },
});
/** ?new=true opens the New invoice or New request box, so the side menu's Create can open it from any page. */
const newSearch = (s: Record<string, unknown>): { new?: boolean } => (s.new === true || s.new === "true" ? { new: true } : {});
const invoicesList = createRoute({
  getParentRoute: () => app,
  path: "/invoices",
  // clientId: the client page's + opens the new invoice box with that client picked.
  validateSearch: (s: Record<string, unknown>): { new?: boolean; clientId?: string } => ({
    ...newSearch(s),
    ...(typeof s.clientId === "string" && s.clientId ? { clientId: s.clientId } : {}),
  }),
  component: () => (
    <Allowed action="invoice.read">
      <InvoicesList />
    </Allowed>
  ),
});
/** A Home To do item as a list to fix from (screens/TodoFix.tsx). Each list checks its own access on the server. */
const todoFix = createRoute({
  getParentRoute: () => app,
  path: "/todo/$kind",
  component: () => (
    <Allowed action="company.read">
      <TodoFix />
    </Allowed>
  ),
});
const requestsList = createRoute({
  getParentRoute: () => app,
  path: "/requests",
  validateSearch: newSearch,
  component: () => (
    <Allowed action="lead.read">
      <RequestsPage />
    </Allowed>
  ),
});
const team = createRoute({
  getParentRoute: () => app,
  path: "/hr/team",
  validateSearch: (s: Record<string, unknown>): { invite?: boolean } =>
    s.invite === true || s.invite === "true" ? { invite: true } : {},
  component: () => (
    <Allowed action="people.read">
      <HrTabs>
        <Team />
      </HrTabs>
    </Allowed>
  ),
});

// Clients, jobs, the schedule and visits (draft 2).
const clientPage = createRoute({
  getParentRoute: () => app,
  path: "/clients/$id",
  component: function ClientRoute() {
    const { id } = clientPage.useParams();
    return (
      <Allowed action="client.read">
        <ByTrade agency={<AgencyClient key={id} id={id} />} other={<ClientPage key={id} id={id} />} />
      </Allowed>
    );
  },
});
/** The dispute file: kept off the menus, opened from the client's More menu or an invoice's dispute banner. */
const clientDispute = createRoute({
  getParentRoute: () => app,
  path: "/clients/$id/dispute",
  validateSearch: (s: Record<string, unknown>): { invoice?: string } =>
    typeof s.invoice === "string" && /^[0-9a-f-]{36}$/.test(s.invoice) ? { invoice: s.invoice } : {},
  component: function ClientDisputeRoute() {
    const { id } = clientDispute.useParams();
    const { invoice } = clientDispute.useSearch();
    return (
      <Allowed action="dispute.read">
        <DisputeFilePage key={`${id}:${invoice ?? ""}`} clientId={id} {...(invoice ? { invoiceId: invoice } : {})} />
      </Allowed>
    );
  },
});
const clientEdit = createRoute({
  getParentRoute: () => app,
  path: "/clients/$id/edit",
  component: function ClientEditRoute() {
    const { id } = clientEdit.useParams();
    return (
      <Allowed action="client.update">
        <ClientForm key={id} id={id} />
      </Allowed>
    );
  },
});
const jobNew = createRoute({
  getParentRoute: () => app,
  path: "/jobs/new",
  validateSearch: (s: Record<string, unknown>): { clientId?: string; quoteId?: string } => ({
    ...(typeof s.clientId === "string" && s.clientId ? { clientId: s.clientId } : {}),
    ...(typeof s.quoteId === "string" && s.quoteId ? { quoteId: s.quoteId } : {}),
  }),
  component: function JobNewRoute() {
    const { clientId, quoteId } = jobNew.useSearch();
    return (
      <Allowed action="job.update">
        <JobForm key={quoteId ?? clientId ?? "new"} {...(clientId ? { clientId } : {})} {...(quoteId ? { quoteId } : {})} />
      </Allowed>
    );
  },
});
const jobPage = createRoute({
  getParentRoute: () => app,
  path: "/jobs/$id",
  component: function JobRoute() {
    const { id } = jobPage.useParams();
    return (
      <Allowed action="job.read">
        <JobPage key={id} id={id} />
      </Allowed>
    );
  },
});
const jobEdit = createRoute({
  getParentRoute: () => app,
  path: "/jobs/$id/edit",
  component: function JobEditRoute() {
    const { id } = jobEdit.useParams();
    return (
      <Allowed action="job.update">
        <JobForm key={id} id={id} />
      </Allowed>
    );
  },
});
const visitPage = createRoute({
  getParentRoute: () => app,
  path: "/visits/$ref",
  component: function VisitRoute() {
    const { ref } = visitPage.useParams();
    return (
      <Allowed action="visit.read">
        <VisitPage refId={ref} />
      </Allowed>
    );
  },
});

// Invoices, payments, quotes and leads (draft 3).
const invoicePage = createRoute({
  getParentRoute: () => app,
  path: "/invoices/$id",
  /** then=send opens the Send box on arrival (New invoice's Save and send). */
  validateSearch: (s: Record<string, unknown>): { then?: "send" } => (s.then === "send" ? { then: "send" } : {}),
  component: function InvoiceRoute() {
    const { id } = invoicePage.useParams();
    const { then } = invoicePage.useSearch();
    return (
      <Allowed action="invoice.read">
        <InvoicePage key={id} id={id} {...(then ? { then } : {})} />
      </Allowed>
    );
  },
});
const paymentPage = createRoute({
  getParentRoute: () => app,
  path: "/payments/$id",
  component: function PaymentRoute() {
    const { id } = paymentPage.useParams();
    return (
      <Allowed action="invoice.read">
        <PaymentPage key={id} id={id} />
      </Allowed>
    );
  },
});
const quotesList = createRoute({
  getParentRoute: () => app,
  path: "/quotes",
  // Views List, Pipeline (2026-11-03) and Plan offers (2026-11-04). ?view= keeps the one picked; with none the page
  // opens on the last one.
  validateSearch: (s: Record<string, unknown>): { view?: QuotesView } =>
    s.view === "list" || s.view === "pipeline" || s.view === "offers" ? { view: s.view } : {},
  component: function QuotesRoute() {
    const { view } = quotesList.useSearch();
    return (
      <Allowed action="quote.read">
        <QuotesPage view={view} />
      </Allowed>
    );
  },
});
const quoteNew = createRoute({
  getParentRoute: () => app,
  path: "/quotes/new",
  validateSearch: (s: Record<string, unknown>): { leadId?: string; clientId?: string } => ({
    ...(typeof s.leadId === "string" && s.leadId ? { leadId: s.leadId } : {}),
    ...(typeof s.clientId === "string" && s.clientId ? { clientId: s.clientId } : {}),
  }),
  component: function QuoteNewRoute() {
    const { leadId, clientId } = quoteNew.useSearch();
    return (
      <Allowed action="quote.update">
        <QuoteForm key={leadId ?? clientId ?? "new"} {...(leadId ? { leadId } : {})} {...(clientId ? { clientId } : {})} />
      </Allowed>
    );
  },
});
const quoteEdit = createRoute({
  getParentRoute: () => app,
  path: "/quotes/$id/edit",
  component: function QuoteEditRoute() {
    const { id } = quoteEdit.useParams();
    return (
      <Allowed action="quote.update">
        <QuoteForm key={id} quoteId={id} />
      </Allowed>
    );
  },
});
const quotePage = createRoute({
  getParentRoute: () => app,
  path: "/quotes/$id",
  component: function QuoteRoute() {
    const { id } = quotePage.useParams();
    return (
      <Allowed action="quote.read">
        <QuotePage key={id} id={id} />
      </Allowed>
    );
  },
});

/** The agency's Proposals are its own (draft 13); every other business gets commercial cleaning bids. */
function ProposalsRoute() {
  const { company } = useSession();
  return company.trade === "agency" ? <Proposals /> : <ProposalsPage />;
}
const proposalEdit = createRoute({
  getParentRoute: () => app,
  path: "/proposals/$id",
  component: function ProposalEditRoute() {
    const { id } = proposalEdit.useParams();
    return (
      <Allowed action="quote.update">
        <ProposalBuilder key={id} id={id} />
      </Allowed>
    );
  },
});

const routeTree = rootRoute.addChildren([
  signIn,
  phoneSignIn,
  officeSignIn,
  forgot,
  reset,
  twoStep,
  handoff,
  invite,
  payPage,
  payShort,
  oldInvoiceLink,
  olderInvoiceLink,
  olderQuoteLink,
  reviewPage,
  reviewShort,
  cardSaved,
  saveCard,
  quotePublic,
  quotePreview,
  quoteShort,
  reportPublic,
  myTrades,
  unsubscribe,
  asClient,
  privacy,
  connectClaude,
  app.addChildren([
    viewAs,
    viewAsVisit,
    todoFix,
    page("/", Start),
    page("/home", () => (
      <Allowed action="company.read">
        <ByTrade agency={<AgencyHome />} other={<Home />} />
      </Allowed>
    )),
    page("/my-day", () => <CrewOnly />),
    page("/my-schedule", () => <CrewOnly page="schedule" />),
    page("/my-time-off", () => (
      <Allowed action="myday.read">
        <MyTimeOff />
      </Allowed>
    )),
    crewVisit,
    team,
    page("/hr/payroll", () => (
      <Allowed action="payroll.read">
        <HrTabs>
          <Payroll />
        </HrTabs>
      </Allowed>
    )),
    page("/hr/time", () => (
      <Allowed action="payroll.read">
        <HrTabs>
          <TimeSheet />
        </HrTabs>
      </Allowed>
    )),
    page("/my-pay", () => (
      <Allowed action="mypay.read">
        <MyPay />
      </Allowed>
    )),
    page("/hr/hiring", () => (
      <Allowed action="people.read">
        <HrTabs>
          <Hiring />
        </HrTabs>
      </Allowed>
    )),
    inboxPage,
    page("/schedule/tools", () => (
      <Allowed action="schedule.read">
        <ScheduleTools />
      </Allowed>
    )),
    page("/insights", () => (
      <Allowed action="insights.read">
        <Insights />
      </Allowed>
    )),
    page("/books", () => (
      <Allowed action="books.read">
        <Books />
      </Allowed>
    )),
    page("/head-office", () => (
      <Allowed action="hq.read">
        <HeadOffice />
      </Allowed>
    )),
    page("/security", () => (
      <Allowed action="security.read">
        <Security />
      </Allowed>
    )),
    page("/search", () => (
      <Allowed action="client.read">
        <SearchPhone />
      </Allowed>
    )),
    page("/clients", () => (
      <Allowed action="client.read">
        <ByTrade agency={<AgencyClients />} other={<ClientsList />} />
      </Allowed>
    )),
    // Agency mode (draft 13).
    page("/deals", () => (
      <Allowed action="lead.read">
        <AgencyOnly>
          <Deals />
        </AgencyOnly>
      </Allowed>
    )),
    page("/tasks", () => (
      <Allowed action="client.read">
        <AgencyOnly>
          <Tasks />
        </AgencyOnly>
      </Allowed>
    )),
    page("/projects", () => (
      <Allowed action="job.read">
        <AgencyOnly>
          <Projects />
        </AgencyOnly>
      </Allowed>
    )),
    page("/proposals", () => (
      <Allowed action="quote.read">
        <ProposalsRoute />
      </Allowed>
    )),
    page("/proposals/new", () => (
      <Allowed action="quote.update">
        <ProposalBuilder />
      </Allowed>
    )),
    // Settings that live with their feature (2026-10-29); the old Settings addresses below still land here.
    page("/invoices/settings", () => (
      <Allowed action="settings.read">
        <FeatureSettings title="Invoice and payment settings" back="/invoices" backLabel="All invoices">
          <SectionMessages section="invoices" />
          <MoneySettings />
        </FeatureSettings>
      </Allowed>
    )),
    page("/quotes/settings", () => (
      <Allowed action="settings.read">
        <FeatureSettings title="Quote settings" back="/quotes" backLabel="All quotes">
          <SectionMessages section="quotes" />
          <PriceSettings />
        </FeatureSettings>
      </Allowed>
    )),
    page("/schedule/settings", () => (
      <Allowed action="settings.read">
        <FeatureSettings title="Schedule settings" back="/schedule" backLabel="Schedule">
          <SectionMessages section="schedule" />
          <ScheduleSettings />
        </FeatureSettings>
      </Allowed>
    )),
    page("/requests/settings", () => (
      <Allowed action="settings.read">
        <FeatureSettings title="Request settings" back="/requests" backLabel="All requests">
          <SectionMessages section="requests" />
          <WebsiteSettings />
        </FeatureSettings>
      </Allowed>
    )),
    page("/hr/settings", () => (
      <Allowed action="settings.read">
        <FeatureSettings title="HR settings" back="/hr/payroll" backLabel="Payroll">
          <TeamSettings />
        </FeatureSettings>
      </Allowed>
    )),
    page("/marketing/settings", () => (
      <Allowed action="settings.read">
        <FeatureSettings title="Marketing settings" back="/marketing" backLabel="Marketing">
          <ReviewsSettings />
        </FeatureSettings>
      </Allowed>
    )),
    // The receptionist has his own page in the side menu (owner, 2026-11-01); Inbox settings was only him.
    page("/inbox/settings", () => <Moved to="/receptionist" />),
    page("/receptionist", () => (
      <Allowed action="settings.read">
        <ReceptionistPage />
      </Allowed>
    )),
    page("/proposals/settings", () => (
      <Allowed action="settings.read">
        <ProposalSettingsPage />
      </Allowed>
    )),
    proposalEdit,
    page("/results", () => (
      <Allowed action="client.read">
        <AgencyOnly>
          <Results />
        </AgencyOnly>
      </Allowed>
    )),
    page("/clients/new", () => (
      <Allowed action="client.update">
        <ClientForm />
      </Allowed>
    )),
    clientPage,
    clientEdit,
    clientDispute,
    page("/jobs", () => (
      <Allowed action="job.read">
        <JobsList />
      </Allowed>
    )),
    jobNew,
    jobPage,
    jobEdit,
    page("/schedule", () => (
      <Allowed action="schedule.read">
        <Schedule />
      </Allowed>
    )),
    visitPage,
    invoicesList,
    invoicePage,
    page("/payments", () => (
      <Allowed action="invoice.read">
        <PaymentsPage />
      </Allowed>
    )),
    paymentPage,
    quotesList,
    quoteNew,
    quotePage,
    quoteEdit,
    // The pipeline moved into Quotes (2026-10-28), its own view since 2026-11-03. Old links still land on it.
    page("/pipeline", () => <Navigate to="/quotes" search={{ view: "pipeline" }} replace />),
    page("/marketing", () => (
      <Allowed action="client.read">
        <Marketing />
      </Allowed>
    )),
    requestsList,
    requestPage,
    sectionMessagePage("/quotes/settings/messages/$kind", "quotes"),
    sectionMessagePage("/invoices/settings/messages/$kind", "invoices"),
    sectionMessagePage("/schedule/settings/messages/$kind", "schedule"),
    sectionMessagePage("/requests/settings/messages/$kind", "requests"),
    settings.addChildren([
      setting("/", SettingsOverview),
      setting("/business", BusinessInfo),
      setting("/money", () => <Moved to="/invoices/settings" />),
      setting("/team", () => <Moved to="/hr/settings" />),
      setting("/alerts", () => (
        <Allowed action="settings.read">
          <AlertsSettings />
        </Allowed>
      )),
      setting("/marketing", () => <Moved to="/marketing/settings" />),
      setting("/prices", () => <Moved to="/quotes/settings" />),
      setting("/eunice", () => <Moved to="/receptionist" />),
      setting("/schedule", () => <Moved to="/schedule/settings" />),
      setting("/website", () => <Moved to="/requests/settings" />),
      setting("/automations", () => (
        <Allowed action="settings.read">
          <AutomationsSettings />
        </Allowed>
      )),
      automationPage,
      setting("/messages", () => (
        <Allowed action="outbox.read">
          <MessagesSettings />
        </Allowed>
      )),
      setting("/data", () => (
        <Allowed action="client.bulk">
          <DataSettings />
        </Allowed>
      )),
      setting("/deleted", () => (
        <Allowed action="client.update">
          <DeletedSettings />
        </Allowed>
      )),
      setting("/keys", () => (
        <Allowed action="keys.read">
          <KeysSettings />
        </Allowed>
      )),
      setting("/plan", () => (
        <Allowed action="franchise.read">
          <PlanSettings />
        </Allowed>
      )),
      setting("/security", SecuritySettings),
      setting("/claude", () => (
        <Allowed action="insights.read">
          <ClaudeSettings />
        </Allowed>
      )),
      setting("/activity", () => <Navigate to="/settings/security" />),
    ]),
  ]),
]);

/** The preview opens on the office sign in, or on a page in its address when served (/pay/demo, /quote/demo, /my). */
const previewStart = () => {
  const { pathname, search } = window.location;
  return pathname === "/" || pathname.endsWith(".html") ? "/sign-in/office" : `${pathname}${search}`;
};

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  ...(__PREVIEW__ ? { history: createMemoryHistory({ initialEntries: [previewStart()] }) } : {}),
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
